/**
 * <volume-tier-price> — subscription volume-tier price display.
 *
 * A self-contained custom element (extends HTMLElement so `connectedCallback`
 * always fires, with no hydration/island dependency). Safe to render many times
 * on one page — e.g. every card in a collection grid — because the cart is
 * fetched once and shared across all instances via `volume-tier.js`.
 *
 * Markup (rendered by `snippets/volume-tier-price.liquid`):
 *
 *   <volume-tier-price
 *     data-sub-price="4050"        {# selling-plan price in cents #}
 *     data-onetime-price="4500"    {# variant price in cents #}
 *     data-add-quantity="1">       {# units this card would add; optional, default 1 #}
 *     <span data-vtp-price>$40.50</span>
 *     <s data-vtp-onetime>$45.00</s>
 *   </volume-tier-price>
 *
 * The subscription price shown is the selling-plan price with the applicable
 * volume-tier percentage applied on top; the one-time price is always shown
 * struck through beside it.
 *
 * @module volume-tier-price
 */

import {
  applyTierDiscount,
  getCartSubscriptionQty,
  money,
  onCartQtyChange,
  tierPercentFor,
} from '@theme/volume-tier';

class VolumeTierPrice extends HTMLElement {
  /** @type {(() => void) | null} */
  #unsubscribe = null;

  connectedCallback() {
    // Re-render on every cart change (add-to-cart from any card, cart drawer…).
    this.#unsubscribe = onCartQtyChange(() => this.#render());
    this.#render();
  }

  disconnectedCallback() {
    this.#unsubscribe?.();
    this.#unsubscribe = null;
  }

  /**
   * The number of subscription units this card would add to the cart. On a PDP
   * this can be driven by a quantity selector; on a collection card it is 1.
   * @returns {number}
   */
  get #addQuantity() {
    return Math.max(1, Number(this.dataset.addQuantity) || 1);
  }

  async #render() {
    const subPrice = Number(this.dataset.subPrice);
    const onetime = Number(this.dataset.onetimePrice);
    if (!Number.isFinite(subPrice)) return;

    const cartQty = await getCartSubscriptionQty();
    const projected = cartQty + this.#addQuantity;
    const pct = tierPercentFor(projected);
    const discounted = applyTierDiscount(subPrice, pct);

    const priceEl = this.querySelector('[data-vtp-price]');
    const onetimeEl = this.querySelector('[data-vtp-onetime]');

    if (priceEl) priceEl.textContent = money(discounted);
    if (onetimeEl && Number.isFinite(onetime)) onetimeEl.textContent = money(onetime);

    // Expose the active tier for styling / analytics hooks.
    this.dataset.activeTierPct = String(pct);
  }
}

if (!customElements.get('volume-tier-price')) {
  customElements.define('volume-tier-price', VolumeTierPrice);
}
